import { Button, Input, message } from "antd";
import styles from "./AllHandbooksPage.module.css";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useAddHandbooksListMutation,
  useEditHandbooksListMutation,
  useLazyGetHandbooksListQuery,
  useRemoveHandbooksListMutation,
} from "features/handbookList/api/handbooksListApi";
import { HandbookList } from "features/handbookList/model/handbookList";
import { CustomTable } from "shared/ui/customTable/CustomTable";
import HandbookFormModal from "features/handbook/ui/form/HandbookFormModal";
import { selectApiVersion } from "shared/store/configSlice";
import { useSelector } from "react-redux";
import { useLazyGetServiceListQuery } from "features/service/api/serviceListApi";
import { useSearchPagination } from "shared/hooks/useSearchPagination";
import { useConfirmDelete } from "shared/hooks/useConfirmDelete";

const DEFAULT_PAGE_SIZE = 10;

export default function AllHandbooksPage() {
  const { t, i18n } = useTranslation();
  const apiVersion = useSelector(selectApiVersion);
  const confirmDelete = useConfirmDelete();

  const {
    pagination,
    search,
    debouncedSearch,
    handleChangeSearch,
    handlePageChange,
  } = useSearchPagination({ defaultPageSize: DEFAULT_PAGE_SIZE });

  const [fetchHandbooks, { data, isFetching }] = useLazyGetHandbooksListQuery();
  const [add, { isLoading: isAdding }] = useAddHandbooksListMutation();
  const [edit, { isLoading: isEditing }] = useEditHandbooksListMutation();
  const [remove] = useRemoveHandbooksListMutation();

  const [fetchServices, { data: servicesData }] = useLazyGetServiceListQuery();

  useEffect(() => {
    fetchServices({ page: 1, size: 200 });
  }, [fetchServices]);

  const serverFilter = useMemo(
    () => ({
      page: pagination.current ?? 1,
      size: pagination.pageSize ?? DEFAULT_PAGE_SIZE,
      deleted: false,
      name: debouncedSearch || "",
    }),
    [pagination.current, pagination.pageSize, debouncedSearch],
  );

  useEffect(() => {
    fetchHandbooks(serverFilter);
  }, [fetchHandbooks, serverFilter, apiVersion]);

  const rows: HandbookList[] = data?.res?.content ?? [];
  const total = data?.res?.totalElements ?? 0;
  const currentPage = pagination.current ?? 1;
  const currentSize = pagination.pageSize ?? DEFAULT_PAGE_SIZE;

  const serviceOptions =
    (servicesData?.res?.content ?? [])
      .filter((s: any) => typeof s.code === "string" && s.code.trim() !== "")
      .map((s: any) => ({
        value: String(s.code),
        label: i18n.language?.startsWith("ky") ? s.nameKy : s.nameRu,
      })) ?? [];

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<HandbookList | null>(null);

  const openCreate = useCallback(() => {
    setEditingRow(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((row: HandbookList) => {
    setEditingRow(row);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const handleSubmit = useCallback(
    async (payload: any) => {
      try {
        const dto = {
          code: payload.code,
          nameRu: payload.nameRu,
          nameKy: payload.nameKy,
          serviceCodes: payload.serviceCodes ?? [],
        };
        if (editingRow?.code) {
          await edit({ body: dto }).unwrap();
          message.success("Запись обновлена");
        } else {
          await add({ body: dto }).unwrap();
          message.success("Запись создана");
        }
        setModalOpen(false);
        await fetchHandbooks(serverFilter);
      } catch (e) {
        message.error("Не удалось сохранить");
        console.error(e);
      }
    },
    [add, edit, editingRow, fetchHandbooks, serverFilter],
  );

  const handleDelete = useCallback(
    (row: HandbookList) => {
      confirmDelete({
        content: row.nameRu || row.nameKy || `#${row.code}`,
        onOk: async () => {
          try {
            await remove({ code: row.code }).unwrap();
            await fetchHandbooks(serverFilter);
            message.success("Запись удалена");
          } catch {
            message.error("Не удалось удалить");
          }
        },
      });
    },
    [confirmDelete, remove, fetchHandbooks, serverFilter],
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.title}>
          {t("handbook.table.allHandbooks")} - {total}
        </div>
        <div className={styles.search}>
          <div className={styles.searchCard}>
            <Input
              placeholder={t("handbook.table.search", "Поиск")}
              value={search}
              size="large"
              allowClear
              onChange={(e) => handleChangeSearch(e.target.value)}
            />
          </div>
          <Button
            type="primary"
            size="large"
            onClick={openCreate}
            style={{ marginLeft: 10 }}
          >
            {t("handbook.table.create", "Создать")}
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        <CustomTable
          data={rows}
          isFetching={isFetching}
          pagination={{
            current: currentPage,
            pageSize: currentSize,
            total,
          }}
          onPageChange={handlePageChange}
          onEdit={openEdit}
          onDelete={handleDelete}
          showService
        />
      </div>

      <HandbookFormModal
        open={modalOpen}
        loading={isAdding || isEditing}
        initial={editingRow}
        onCancel={closeModal}
        onSubmit={handleSubmit}
        isAllHandbooks
        isAllServices={false}
        serviceOptions={serviceOptions}
      />
    </div>
  );
}
